import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";

const POLL_MS = 30_000;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export function NotificationBell({ dark = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      // Non-fatal: user may not be logged in yet
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openOne = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      setItems((xs) => xs.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
      setUnread((c) => Math.max(0, c - 1));
    }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch {}
    setItems((xs) => xs.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
  };

  const btnBase = "relative inline-flex items-center justify-center h-9 w-9 rounded-md border transition-colors";
  const btnTheme = dark
    ? "border-white/10 text-slate-200 hover:bg-white/5"
    : "border-slate-200 text-slate-600 hover:bg-slate-100";

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${btnBase} ${btnTheme}`}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={open}
        data-testid="notifications-bell"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center shadow-sm"
            data-testid="notifications-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 w-[320px] sm:w-[380px] max-h-[70vh] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl z-50"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900">Notifications</div>
            <button
              onClick={markAll}
              disabled={unread === 0}
              className="text-xs text-[hsl(var(--navy))] font-medium hover:underline disabled:opacity-40 disabled:no-underline"
              data-testid="notifications-mark-all-read"
            >
              Mark all read
            </button>
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-slate-500" data-testid="notifications-empty">
              You're all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => {
                const inner = (
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.is_read ? "bg-transparent" : "bg-red-500"}`} />
                    <div className="min-w-0 flex-1">
                      <div className={`text-[13px] leading-snug ${n.is_read ? "text-slate-600" : "text-slate-900 font-medium"}`}>{n.title}</div>
                      {n.body && (
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</div>
                      )}
                      <div className="text-[11px] text-slate-400 mt-1">{timeAgo(n.created_at)} ago</div>
                    </div>
                  </div>
                );
                const classes = `w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${n.is_read ? "" : "bg-sky-50/40"}`;
                return (
                  <li key={n.id} data-testid={`notification-item-${n.id}`}>
                    {n.link ? (
                      <Link to={n.link} onClick={() => { openOne(n); setOpen(false); }} className={classes}>
                        {inner}
                      </Link>
                    ) : (
                      <button type="button" onClick={() => openOne(n)} className={classes}>
                        {inner}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
